import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Whitby = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

  const cheapestLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.whitby:About Whitby Paragraph 1">
            Whitby, located in{' '}
            <LinkText href="/rental-listings/ontario">Ontario</LinkText>, offers
            a blend of suburban tranquility and urban convenience within the{' '}
            <LinkText href={cheapestLink}>Greater Toronto Area</LinkText>.
            Nestled along the shores of Lake Ontario, Whitby boasts picturesque
            waterfront views and serene landscapes that provide residents with
            opportunities for outdoor enjoyment and relaxation. This charming
            town is characterized by its family-friendly neighborhoods, quality
            schools, and a strong sense of community.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.whitby:About Whitby Paragraph 2">
            Whitby’s appeal lies in its harmonious combination of residential
            comfort and access to amenities of the Greater Toronto Area. The
            town features a thriving downtown area with a variety of shops,
            restaurants, and cultural attractions that enhance the overall
            quality of life for its residents. Whether you’re exploring local
            parks, participating in community events, or simply enjoying the
            scenic beauty, living in Whitby offers a well-rounded lifestyle.
            This town’s proximity to both natural beauty and urban conveniences
            contributes to its status as a desirable place to call home.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,700 - $2,600',
          bed2: '$2,000 - $3,200',
          bed3: '$2,700 - $3,900'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e5%a4%9a%e4%bc%a6%e5%a4%9a/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.whitby:Apartment Hunting in Whitby Paragraph 1">
            {{ cityName }} is an attractive city of Ontario known for its
            thriving community and diverse housing options. While rental prices
            in {{ cityName }} are generally more affordable compared to Toronto,
            they have been steadily increasing over time. As a result, finding{' '}
            <LinkText href={links.under1000}>
              cheap rentals in {{ cityName }}
            </LinkText>
            , especially{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>
            , can be challenging. However, to initiate your search, you can
            refer to this guide highlighting{' '}
            <LinkText href={cheapestLink}>
              affordable neighborhoods in the Greater Toronto Area (GTA)
            </LinkText>{' '}
            that may offer relatively more budget-friendly rental options.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whitby:Apartments for rent Whitby Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Whitby
              </LinkText>
              , particularly in low-rise buildings, are available but less
              common than in larger cities. Rental prices vary based on unit
              size and included amenities. They are typically an affordable
              option for singles, young professionals, and small families.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whitby:Houses for rent Whitby Paragraph">
              <LinkText href={links.house}>Houses for rent in Whitby</LinkText>{' '}
              are more popular for families since it’s suitable for those with a
              higher budget, or who have multiple roommates. Both partial houses
              and entire houses for rent are common, as well as both new builds
              and older homes from the city’s earlier days. The prices can vary
              significantly based on the size, condition, and neighborhood, but
              they generally command higher rental prices due to the additional
              space and privacy they offer.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whitby:Condos for rent Whitby Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Whitby
              </LinkText>{' '}
              are increasingly popular, especially for individuals who prefer a
              maintenance-free lifestyle with added amenities. While condos can
              be pricier due to these additional facilities and community
              features, they appeal to professionals or small families looking
              for modern, streamlined living. These types of rental
              accommodation are perfect for those who like to be in the centre
              of the action, and don’t need as much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.whitby:Short term rentals Whitby Paragraph">
              Whitby, with its proximity to various employment areas and
              educational institutions, does attract individuals, including
              students and transient workers, who may require short-term rental
              arrangements. However, while there is some demand for{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>,
              especially given its location within commuting distance to several
              universities and colleges, they are not the predominant market in
              the area. The town’s growing community and family-friendly
              environment mean that many renters typically seek longer-term
              stability. Furthermore, local regulations may impact the
              availability of short-term rentals. Nonetheless, the added
              flexibility is an appealing prospect for many renters, and a key
              factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are suitable
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.whitby:Living in Whitby Paragraph">
          Living in Whitby, Ontario, is an experience steeped in a friendly,
          community-oriented environment that embraces both individuals and
          families. As a town with a significant focus on residential
          development, it offers a range of rental options suitable for various
          lifestyles, including families, young professionals, and commuters who
          appreciate its blend of suburban tranquility and proximity to{' '}
          <LinkText href={neighborhoodsLink}>Toronto</LinkText>. Residents enjoy
          a wealth of recreational activities with numerous parks, sports
          facilities, and cultural events, underscored by the town’s commitment
          to green spaces and community festivals like the annual Whitby Harbour
          Day. The dynamic arts and culture scene, along with historical
          attractions like the Whitby History Museum, add depth to the town’s
          social fabric, ensuring that there are diverse offerings for leisure
          and learning. Whitby’s blend of accessible housing, community spirit,
          and an array of activities establishes it as an inviting place to
          live, fostering a sense of belonging and an easy-going lifestyle.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.whitby:Popular Neighbourhoods Whitby Paragraph">
          Like any city, Whitby is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Port Whitby, Brooklin, Downtown Whitby, Blue Grass Meadows, Downtown
          Whitby, Pringle Creek, Williamsburg, and Taunton North. For more info
          about Whitby, you can consult this guide on{' '}
          <LinkText href={neighborhoodsLink}>Toronto neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.whitby:Local Landmarks Whitby Paragraph">
          Whitby, Ontario, harbors several landmarks that encapsulate its rich
          historical heritage and natural beauty, drawing visitors and residents
          alike. The prominent Whitby Courthouse Theatre, a historic building
          and a hub for local performing arts, stands as a testament to the
          town’s cultural vitality. The Trafalgar Castle School, an impressive
          19th-century castle and an architectural marvel, now serves as an
          all-girls’ school but remains a point of interest for its historical
          significance. Nature enthusiasts gravitate towards the Lynde Shores
          Conservation Area, known for its captivating wildlife and walking
          trails, offering a quick escape into nature’s embrace. Additionally,
          Port Whitby Marina, a bustling recreational harbour overlooking Lake
          Ontario, not only offers scenic views but also serves as a reminder of
          the town’s strategic maritime history. Each of these landmarks
          contributes to Whitby’s unique identity, blending its historical roots
          with present-day community life.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Whitby
